import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Search, ChevronDown, Check, Filter as FilterIcon } from 'lucide-react';
import Icon from 'shared/components/Icon';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import GroupsFilterModule from 'features/activity/components/GroupsFilterModule';
import { activitySearchInputClass, activitySearchWrapperClass, activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';

const GroupsFilterBar = ({
  groupKey,
  onRefresh,
  onTimeFilterChange,
  onSearchChange,
  searchTerm = '',
  selectedTimePreset = '30d',
  lastRefreshTime = null,
  showTitle = true,
  showRefresh = true,
  onEditColumns = () => {},
  filters = [],
  onFiltersChange = () => {},
  teamId = null,
  // Group dropdown props (when used inside Users context)
  groupKeys = [],
  activeGroupTab = null,
  onGroupTabChange = null,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onDoc = (e) => {
      if (!dropdownRef.current?.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dropdownOpen]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearch = (value) => {
    const trimmedValue = (value || '').trim();
    setLocalSearchTerm(trimmedValue);
    onSearchChange(trimmedValue);
  };

  const handleSearchClear = () => {
    setLocalSearchTerm('');
    onSearchChange('');
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const formatLastRefresh = () => {
    if (!lastRefreshTime) return '';
    const now = new Date();
    const diffMins = Math.floor((now - lastRefreshTime) / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  };

  const groupKeyLabel = groupKey
    ? groupKey.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Groups';

  return (
    <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-[color:var(--border-default)] bg-surface px-3 sm:px-4 pb-2 pt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Title and Refresh Row */}
      <div className="flex flex-wrap items-center gap-2">
        {onGroupTabChange && groupKeys.length > 0 ? (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-1 text-xl font-medium text-[color:var(--text-primary)] outline-none"
            >
              {activeGroupTab === null ? 'Users' : groupKeyLabel}
              <ChevronDown
                size={16}
                strokeWidth={2}
                className="text-[color:var(--text-muted)] transition-transform"
                style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-1 min-w-[160px] rounded-lg border border-[color:var(--border-default)] bg-surface shadow-md z-50 py-1">
                <button
                  type="button"
                  onClick={() => { onGroupTabChange(null); setDropdownOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-sunken)] transition-colors"
                >
                  <span className="w-4 flex-shrink-0">
                    {activeGroupTab === null && <Check size={13} strokeWidth={2.5} className="text-[color:var(--text-primary)]" />}
                  </span>
                  Users
                </button>
                {groupKeys.map(({ group_key }) => {
                  const label = group_key.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <button
                      key={group_key}
                      type="button"
                      onClick={() => { onGroupTabChange(group_key); setDropdownOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-sunken)] transition-colors"
                    >
                      <span className="w-4 flex-shrink-0">
                        {activeGroupTab === group_key && <Check size={13} strokeWidth={2.5} className="text-[color:var(--text-primary)]" />}
                      </span>
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : showTitle ? (
          <h1 className="text-xl font-medium text-[color:var(--text-primary)]">{groupKeyLabel}</h1>
        ) : null}
        {showRefresh && (
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
            title={`Refresh ${groupKeyLabel}`}
          >
            <RefreshCw className="h-4 w-4" />
            {lastRefreshTime && (
              <span className="text-xs">Updated {formatLastRefresh()}</span>
            )}
          </button>
        )}
      </div>

      {/* Toolbar Row */}
      <div className="flex flex-wrap items-center gap-2">
        <EventsTimeFilter onTimeFilterChange={onTimeFilterChange} selectedPreset={selectedTimePreset} />

        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className={activityToolbarButtonClass}
        >
          <FilterIcon size={14} color="var(--text-muted)" />
          {showFilters ? 'Hide Filter' : 'Show Filter'}
          {filters.length > 0 && (
            <span className="rounded-full bg-[color:var(--text-primary)] px-1.5 text-[10px] font-semibold text-ink-onBrand">
              {filters.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onEditColumns}
          className={activityToolbarButtonClass}
          title="Edit columns"
        >
          <Icon name="edit_pencil" size={14} color="var(--text-muted)" />
          Edit Columns
        </button>

        <div className={`${activitySearchWrapperClass} w-[240px]`}>
          <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={`Search by ${groupKeyLabel.toLowerCase()} ID...`}
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                handleSearch(e.target.value || '');
              }
            }}
            className={activitySearchInputClass}
          />
          {localSearchTerm && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="flex-none text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
              aria-label="Clear search"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Filters panel */}
      <div className={`border-t border-[color:var(--border-default)] bg-surface py-4 transition-all duration-200 ${showFilters ? 'block' : 'hidden'}`}>
        <GroupsFilterModule
          filters={filters}
          onFiltersChange={onFiltersChange}
          teamId={teamId}
          groupKey={groupKey}
        />
      </div>
    </div>
  );
};

export default GroupsFilterBar;
